import { apiFetch } from './client';

export type CashflowGranularity = 'DAY' | 'WEEK' | 'MONTH' | 'SESSION';

export interface CashflowSummaryBucket {
    periodStart?: string | null;
    periodEnd?: string | null;
    cashSessionId?: number | null;
    label?: string | null;
    totalAmount?: number;
    paymentCount?: number;
    amountByMethod?: Record<string, number>;
    amountByChannel?: Record<string, number>;
    amountByOperator?: Record<string, number>;
}

export interface CashflowSummaryResponse {
    totalAmount?: number;
    totalCount?: number;
    ordersCount?: number;
    averagePerPayment?: number;
    averagePerOrder?: number;
    estimatedVat?: number;
    amountByMethod?: Record<string, number>;
    amountByChannel?: Record<string, number>;
    amountByOperator?: Record<string, number>;
    buckets: CashflowSummaryBucket[];
}

export interface CashflowRecordDTO {
    id: number;
    orderId?: string | null;
    channel?: string | null;
    method?: string | null;
    amount?: number;
    reference?: string | null;
    note?: string | null;
    receivedAt?: string | null;
    recordedAt?: string | null;
    recordedBy?: string | null;
    cashSessionId?: number | null;
    voidedAt?: string | null;
    voidedBy?: string | null;
}

export interface CashflowRecordsResponse {
    records: CashflowRecordDTO[];
    totalCount: number;
    totalAmount?: number;
}

export interface CashSessionDTO {
    id: number;
    openedAt?: string | null;
    openedBy?: string | null;
    openingFloat?: number | null;
    closedAt?: string | null;
    closedBy?: string | null;
    closingCash?: number | null;
    closingNotes?: string | null;
    open: boolean;
    paymentsCount?: number;
    paymentsAmount?: number;
}

export interface CashflowSummaryParams {
    from?: string;
    to?: string;
    granularity?: CashflowGranularity;
    channel?: string;
    method?: string;
    operator?: string;
}

export interface CashflowRecordsParams {
    from?: string;
    to?: string;
    channel?: string;
    method?: string;
    operator?: string;
    page?: number;
    size?: number;
}

export interface CashSessionOpenPayload {
    openingFloat?: number;
}

export interface CashSessionClosePayload {
    closingCash?: number;
    notes?: string;
}

export interface CashflowVoidPayload {
    reason?: string;
}

export interface CashflowManualEntryPayload {
    amount: number;
    method?: string;
    channel?: string;
    reference?: string;
    note?: string;
    receivedAt?: string;
    cashSessionId?: number;
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
            return;
        }
        search.set(key, String(value));
    });
    const serialized = search.toString();
    return serialized ? `?${serialized}` : '';
}

export async function fetchCashflowSummary(params: CashflowSummaryParams = {}): Promise<CashflowSummaryResponse> {
    const query = buildQuery({
        from: params.from,
        to: params.to,
        granularity: params.granularity,
        channel: params.channel,
        method: params.method,
        operator: params.operator,
    });
    const res = await apiFetch(`/api/tenant/cashflow/summary${query}`);
    if (!res.ok) {
        throw new Error(await res.text() || 'Impossibile recuperare il riepilogo cassa');
    }
    return res.json();
}

export async function fetchCashflowRecords(params: CashflowRecordsParams = {}): Promise<CashflowRecordsResponse> {
    const query = buildQuery({
        from: params.from,
        to: params.to,
        channel: params.channel,
        method: params.method,
        operator: params.operator,
        page: params.page,
        size: params.size,
    });
    const res = await apiFetch(`/api/tenant/cashflow/records${query}`);
    if (!res.ok) {
        throw new Error(await res.text() || 'Impossibile recuperare i movimenti di cassa');
    }
    return res.json();
}

export async function listCashSessions(): Promise<CashSessionDTO[]> {
    const res = await apiFetch('/api/tenant/cashflow/sessions');
    if (!res.ok) {
        throw new Error(await res.text() || 'Impossibile recuperare le sessioni cassa');
    }
    return res.json();
}

export async function exportCashflowRecords(params: CashflowRecordsParams = {}): Promise<Blob> {
    const query = buildQuery({
        from: params.from,
        to: params.to,
        channel: params.channel,
        method: params.method,
        operator: params.operator,
    });
    const res = await apiFetch(`/api/tenant/cashflow/records/export${query}`, {
        headers: {
            Accept: 'text/csv',
        },
    });
    if (!res.ok) {
        throw new Error(await res.text() || 'Impossibile esportare i movimenti di cassa');
    }
    return res.blob();
}

export async function openCashSession(payload: CashSessionOpenPayload = {}): Promise<CashSessionDTO> {
    const res = await apiFetch('/api/tenant/cashflow/sessions', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        throw new Error(await res.text() || 'Impossibile aprire la sessione di cassa');
    }
    return res.json();
}

export async function closeCashSession(sessionId: number, payload: CashSessionClosePayload = {}): Promise<CashSessionDTO> {
    const res = await apiFetch(`/api/tenant/cashflow/sessions/${sessionId}/close`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        throw new Error(await res.text() || 'Impossibile chiudere la sessione di cassa');
    }
    return res.json();
}

export async function voidCashflowPayment(paymentId: number, payload: CashflowVoidPayload = {}): Promise<void> {
    const res = await apiFetch(`/api/tenant/cashflow/records/${paymentId}/void`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        throw new Error(await res.text() || 'Impossibile stornare l\'incasso');
    }
}

export async function recordManualPayment(payload: CashflowManualEntryPayload): Promise<CashflowRecordDTO> {
    const res = await apiFetch('/api/tenant/cashflow/records/manual', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    if (!res.ok) {
        throw new Error(await res.text() || 'Impossibile registrare il pagamento');
    }
    return res.json();
}
